import { useCallback, useEffect, useState } from "react";
import Tooltip from "bootstrap/js/dist/tooltip";

/**
 * Hook to initialize Bootstrap tooltips on React elements.
 *
 * @param title - Optional dynamic title. If provided, updates the tooltip content when changed.
 *                If not provided, the tooltip uses the element's `title` attribute.
 * @returns A callback ref to attach to the target element.
 *
 * @example
 * // Basic usage with static title from element attribute
 * function MyComponent() {
 *   const tooltipRef = useTooltip<HTMLButtonElement>();
 *   return (
 *     <button ref={tooltipRef} data-bs-toggle="tooltip" title="Click me">
 *       Hover for tooltip
 *     </button>
 *   );
 * }
 *
 * @example
 * // With dynamic title that updates
 * function MyComponent({ count }: { count: number }) {
 *   const tooltipRef = useTooltip<HTMLSpanElement>(`Count: ${count}`);
 *   return (
 *     <span ref={tooltipRef} data-bs-toggle="tooltip" title={`Count: ${count}`}>
 *       {count}
 *     </span>
 *   );
 * }
 *
 * @remarks
 * This hook uses a callback ref pattern instead of useRef because:
 * - useRef.current changes don't trigger re-renders or effect re-runs
 * - Using useRef.current as a useEffect dependency doesn't work reliably
 * - Callback refs properly handle conditionally rendered elements
 *
 * The callback ref stores the element in state, which triggers the effect
 * to initialize the tooltip as soon as the element mounts.
 */
export default function useTooltip<T extends HTMLElement>(title?: string) {
  const [element, setElement] = useState<T | null>(null);

  // Use callback ref to capture the element when it mounts
  const elemRef = useCallback((node: T | null) => {
    setElement(node);
  }, []);

  useEffect(() => {
    if (element) {
      Tooltip.getOrCreateInstance(element);
    }

    return function () {
      if (element) Tooltip.getInstance(element)?.dispose();
    };
  }, [element]);

  useEffect(() => {
    if (element && title) {
      const tooltipInstance = Tooltip.getInstance(element);
      if (tooltipInstance) {
        tooltipInstance.setContent({
          ".tooltip-inner": title,
        });
      }
    }
  }, [element, title]);

  return elemRef;
}
